import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector, connect, useDispatch } from "react-redux";
import { Loading } from "@pages/Other/Loading";
import { userActions } from "../../_actions/user.actions";
import { errorActions } from "../../_actions/error.actions";
import ImageSlider from "./ImageSlider";
import { Lock, User, Sun, Moon, Eye, EyeOff, Mail } from "lucide-react";
import VerificationEmail from "./VerificationEmail";
import OverlayContainer from "@components/Overlay/OverlayContainer";

function LoginPage(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { error: globalError } = useSelector((state) => state.error);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendVerification, setResendVerification] = useState(false);
  const [loginInfo, setLoginInfo] = useState({
    email: "",
    password: "",
    managePassword: false,
  });

  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("theme") === "dark";
  });

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("theme", newMode ? "dark" : "light");
  };

  const themeClass = darkMode ? "dark" : "light";
  const bgClass = darkMode ? "bg-slate-950 text-white" : "bg-gradient-to-br from-indigo-50 via-slate-100 to-blue-50 text-slate-900";
  const cardClass = darkMode 
    ? "bg-slate-900/80 border-slate-800 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)]" 
    : "bg-white/95 border-slate-200/80 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.1)]";
  const formSectionClass = darkMode ? "bg-slate-900/40" : "bg-slate-50/50";
  const titleClass = darkMode ? "text-white" : "text-slate-900";
  const textMutedClass = darkMode ? "text-slate-400" : "text-slate-500";
  const borderClass = darkMode ? "border-slate-800" : "border-slate-200";
  const inputClass = darkMode
    ? "w-full !pl-12 !pr-4 !py-3.5 !bg-slate-950/50 hover:!bg-slate-950/80 focus:!bg-slate-950 !border !border-slate-800 focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-500/20 !text-white placeholder-slate-600 !rounded-2xl transition-all duration-300 text-sm outline-none shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]"
    : "w-full !pl-12 !pr-4 !py-3.5 !bg-slate-100/50 hover:!bg-slate-100/85 focus:!bg-white !border !border-slate-200 focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-500/20 !text-slate-900 placeholder-slate-400 !rounded-2xl transition-all duration-300 text-sm outline-none shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)]";
  const inputPasswordClass = darkMode
    ? "w-full !pl-12 !pr-16 !py-3.5 !bg-slate-950/50 hover:!bg-slate-950/80 focus:!bg-slate-950 !border !border-slate-800 focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-500/20 !text-white placeholder-slate-600 !rounded-2xl transition-all duration-300 text-sm outline-none shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]"
    : "w-full !pl-12 !pr-16 !py-3.5 !bg-slate-100/50 hover:!bg-slate-100/85 focus:!bg-white !border !border-slate-200 focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-500/20 !text-slate-900 placeholder-slate-400 !rounded-2xl transition-all duration-300 text-sm outline-none shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)]";
  const iconClass = darkMode ? "text-slate-500 group-focus-within:text-blue-500" : "text-slate-400 group-focus-within:text-blue-500";

  const urlParams = new URLSearchParams(window.location.search);
  const myParams = urlParams.get("email");

  useEffect(() => {
    if (myParams) {
      setResendVerification(true);
    }
  }, [myParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!loginInfo.email || !loginInfo.password) {
      setError(t("Please enter both email and password."));
      return;
    }

    setLoading(true);
    try {
      await dispatch(userActions.login(loginInfo.email, loginInfo.password));
      const from = location.state?.from || "/dashboard";
      const courseState = location.state?.course ? { course: location.state.course } : undefined;
      navigate(from, { state: courseState });
    } catch (err) {
      setError(err || t("Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.loggedIn) {
      const from = location.state?.from || "/dashboard";
      const courseState = location.state?.course ? { course: location.state.course } : undefined;
      navigate(from, { state: courseState });
    }
  }, [props.loggedIn]);

  const handleViewPassword = () => {
    setLoginInfo({ ...loginInfo, managePassword: !loginInfo.managePassword });
  };

  return (
    <div className={`LoginPage min-h-screen flex items-center justify-center p-4 relative overflow-hidden select-none ${bgClass}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Floating Theme Toggle Button */}
      <button
        type="button"
        onClick={toggleDarkMode}
        className={`fixed top-6 right-6 z-50 p-3.5 rounded-full border transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 ${
          darkMode
            ? "bg-slate-900 border-slate-800 text-yellow-400 hover:bg-slate-800"
            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
        }`}
        aria-label="Toggle Theme"
      >
        {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Main 3D Card Container */}
      <div className={`w-full max-w-5xl border rounded-[2.5rem] flex flex-col md:flex-row overflow-hidden relative z-10 ${cardClass}`}>
        
        {/* Left Side: 3D Image Slider Section */}
        <div className="w-full md:w-[45%] lg:w-[50%] min-h-[300px] md:min-h-[600px] relative overflow-hidden">
          <ImageSlider />
        </div>

        {/* Right Side: Professional 3D login form */}
        <div className={`w-full md:w-[55%] lg:w-[50%] p-8 sm:p-12 lg:p-16 flex flex-col justify-center ${formSectionClass}`}>
          <div className="w-full max-w-md mx-auto space-y-8">
            
            {/* Header / Brand */}
            <div className="space-y-2">
              <h1 className={`text-3xl sm:text-4xl font-black tracking-tight ${titleClass}`}>
                {t("Welcome Back")}
              </h1>
              <p className={`text-sm font-medium ${textMutedClass}`}>
                Sign in to your account to continue your learning.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email field with 3D inset shadow on focus */}
              <div className="space-y-2">
                <label className={`text-xs font-bold uppercase tracking-wider ${textMutedClass}`}>
                  {t("email")}
                </label>
                <div className="relative group">
                  <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${iconClass}`} />
                  <input
                    type="email"
                    placeholder={t("email")}
                    value={loginInfo.email}
                    className={inputClass}
                    onChange={(e) =>
                      setLoginInfo({ ...loginInfo, email: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Password field */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className={`text-xs font-bold uppercase tracking-wider ${textMutedClass}`}>
                    {t("password")}
                  </label>
                </div>
                <div className="relative group">
                  <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${iconClass}`} />
                  <input
                    type={loginInfo.managePassword ? "text" : "password"}
                    placeholder={t("password")}
                    className={inputPasswordClass}
                    onChange={(e) =>
                      setLoginInfo({ ...loginInfo, password: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    onClick={handleViewPassword}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-blue-500 transition-colors cursor-pointer"
                  >
                    {loginInfo.managePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-red-500 text-xs font-semibold">{error}</p>}

              <div className="flex items-center justify-between text-xs font-semibold">
                <Link to="/forget-password" className="text-blue-500 hover:text-blue-600 transition-colors">
                  {t("forgotPassword")}
                </Link>
              </div>

              {/* Glowing 3D Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full !py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white text-sm font-black rounded-2xl shadow-lg hover:shadow-[0_8px_25px_rgba(79,70,229,0.4)] hover:translate-y-[-2px] active:translate-y-[1px] transition-all duration-300 flex items-center justify-center min-h-[52px]"
              >
                {loading ? <Loading /> : t("Log in")}
              </button>
            </form>

            <div className={`text-center pt-4 border-t ${borderClass}`}>
              <p className={`text-xs font-medium ${textMutedClass}`}>
                {t("notRegistered")}{" "}
                <Link to="/register" className="text-blue-500 hover:text-blue-600 hover:underline font-bold transition-all ml-1">
                  {t("Signup")}
                </Link>
              </p>
            </div>

          </div>
        </div>

      </div>
      {resendVerification &&
        globalError?.message ===
          "You must verify your email address from the message we sent you before performing that action." && (
          <OverlayContainer>
            <VerificationEmail
              setResendVerification={setResendVerification}
              email={loginInfo.email}
              loginInfo={loginInfo}
              setLoginInfo={setLoginInfo}
            />
          </OverlayContainer>
        )}
    </div>
  );
}

function mapStateToProps(state) {
  const { loggedIn } = state.authentication;
  return { loggedIn };
}

const connectedLoginPage = connect(mapStateToProps)(LoginPage);
export default connectedLoginPage;
